import Dialog from "@mui/material/Dialog";
import * as React from "react";
import CloseIcon from "@mui/icons-material/Close";
import { IconButton } from "@mui/material";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import { Document, Image, Page, PDFViewer, StyleSheet, Text, View } from "@react-pdf/renderer";
import GetAppIcon from "@mui/icons-material/GetApp";
import { Button } from "@mui/material";
import { pdf } from "@react-pdf/renderer";
import { countriesCurrency, getCodeColor } from '../../../components'

const CotizacionPDF = ({ apiResponse, themeColor, convertPrice }) => {
	const API_url = process.env.REACT_APP_API_URL;
	const themeConverted = getCodeColor(themeColor);

	const styles = StyleSheet.create({
		page: {
			flexDirection: "column",
			padding: 20,
			fontSize: 10,
			fontFamily: "Helvetica",
			position: "relative",
			bottom: "30",
		},
		headerContainer: {
			flexDirection: "row",
			alignContent: "center",
			paddingBottom: 10,
		},
		headerLeft: {
			flexDirection: "column",
			width: "40%",
			lineHeight: 1.2,
			fontSize: 7,
		},
		logo: {
			width: 150,
			height: 100,
		},
		titleBig: {
			fontFamily: "Helvetica-Bold",
			fontSize: 19,
			letterSpacing: 1,
			margin: 0,
			paddingLeft: 35,
			position: "relative",
			top: "15",
			right: "40",
		},
		titleNumber: {
			fontSize: 18,
			color: "#ff3d3d",
			fontWeight: "bold",
			paddingLeft: 70,
			letterSpacing: 2,
			fontFamily: "Helvetica-Bold",
			position: "relative",
			left: 50,
		},
		subHeaderContainer: {
			flexDirection: "row",
			justifyContent: "space-between",
			paddingRight: "10",
			alignItems: "center",
			position: "relative",
			bottom: 5,
			fontSize: 9,
		},
		tableContainer: {
			width: "100%",
			marginVertical: 10,
			position: "relative",
			bottom: 10,
		},
		tableHeader: {
			flexDirection: "row",
			backgroundColor: themeConverted,
			color: "#fff",
			fontWeight: "bold",
		},
		tableHeaderCell: {
			padding: 5,
			textAlign: "center",
			fontWeight: "bold",
			fontSize: 8,
		},
		tableRow: {
			flexDirection: "row",
		},
		tableCell: {
			padding: 5,
			textAlign: "center",
			fontSize: 8,
		},
		tableCellNro: { flex: 0.5 },
		tableCellCodigo: { flex: 1.5 },
		tableCellDescripcion: { flex: 4, textAlign: "left" },
		tableCellCantidad: { flex: 0.8 },
		tableCellPrecioUnitario: { flex: 1.2 },
		tableCellDescuento: { flex: 1 },
		tableCellPrecio: { flex: 1.2 },
		bottomRowContainer: {
			flexDirection: "row",
			alignItems: "center",
			borderTop: "1px solid #000",
			marginTop: 0,
			position: "relative",
			bottom: 10,
		},
		bottomNoteContainer: {
			backgroundColor: themeConverted,
			flex: 3,
			padding: 10,
			position: "relative",
			bottom: 10,
		},
		bottomNote: {
			color: "#fff",
			fontSize: 7,
			fontWeight: "bold",
			textAlign: "center",
		},
		bottomTotals: {
			flex: 2,
			padding: 5,
			fontSize: 10,
			lineHeight: 1.3,
		},
		bottomTotalsItem: {
			flexDirection: "row",
			justifyContent: "space-between",
			fontSize: 8,
		},
		boldText: {
			fontWeight: "bold",
		},
		bottomDetailsRow: {
			position: "relative",
			bottom: 27,
			left: 5,
			fontSize: 8,
			marginTop: 10,
		},
		signatureContainer: {
			display: "flex",
			flexDirection: "row",
			justifyContent: "space-around",
			marginTop: 30,
		},
		signatureLine: {
			marginBottom: 5,
		},
		signatureBox: {
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
		},
		pageNumbers: {
			position: "absolute",
			bottom: 20,
			right: 20,
			textAlign: "right",
		},
	});

	const calcularVigencia = (fechaInicio, fechaFin) => {
		if (!fechaInicio || !fechaFin) return "";

		// Parse dates
		let inicio, fin;

		if (fechaInicio.includes("/")) {
			const [day, month, year] = fechaInicio.split("/");
			inicio = new Date(year, month - 1, day);
		} else {
			inicio = new Date(fechaInicio);
		}

		if (fechaFin.includes("/")) {
			const [day, month, year] = fechaFin.split("/");
			fin = new Date(year, month - 1, day);
		} else {
			fin = new Date(fechaFin);
		}

		if (isNaN(inicio.getTime()) || isNaN(fin.getTime())) return "";

		const diffMs = fin.getTime() - inicio.getTime();
		const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

		if (diffDays <= 0) return "Vencida";

		if (diffDays < 30) {
			return `${diffDays} día${diffDays !== 1 ? "s" : ""}`;
		} else if (diffDays < 365) {
			const monthDiff = (fin.getFullYear() - inicio.getFullYear()) * 12 + (fin.getMonth() - inicio.getMonth());
			return `${monthDiff} mes${monthDiff !== 1 ? "es" : ""}`;
		} else {
			const years = Math.floor(diffDays / 365);
			return `${years} año${years !== 1 ? "s" : ""}`;
		}
	};

	const getBill = (billStatus) => {
		switch (billStatus) {
			case "enabled":
				return "Facturado";
			case "disabled":
				return "Sin Factura";
			default:
				return "Ambos";
		}
	};

	const getTotalSinFactura = (precioUnitario, descuento, cantidad) => {
		let total;
		if (descuento === 0) {
			total = parseFloat(precioUnitario) * cantidad;
			return total.toFixed(2);
		}
		total = (parseFloat(precioUnitario) - descuento) * cantidad;
		return total.toFixed(2);
	};

	const getTotalConFactura = (precioUnitario, precioFactura, descuento, cantidad) => {
		let total;
		if (descuento === 0) {
			total = (parseFloat(precioUnitario) + parseFloat(precioFactura)) * cantidad;
			return total.toFixed(2);
		}
		total = (parseFloat(precioUnitario) + parseFloat(precioFactura) - descuento) * cantidad;
		return total.toFixed(2);
	};

	const getPrice = (price = 0, itsFacturado = false, priceFa = 0) => {
		const priceConverted = parseFloat(price) + parseFloat(itsFacturado ? priceFa : 0);
		return priceConverted;
	}

	const getTextCurrency = () => {
		const current_code = String(convertPrice('0'));
		const textCurrency = countriesCurrency().find((currency_obj) => current_code.includes(currency_obj?.code));
		return textCurrency?.currency ?? 'Bolivianos';
	}

	const numeroALetras = (numero) => {
		// Format to ensure we have 2 decimal places
		const [intPart, decPart = "00"] = Number(numero).toFixed(2).split(".");
		const num = parseInt(intPart, 10);

		if (num === 0) return `Cero ${decPart}/100 ${getTextCurrency()}`;

		const unidades = ["", "Un", "Dos", "Tres", "Cuatro", "Cinco", "Seis", "Siete", "Ocho", "Nueve"];
		const decenas = [
			"",
			"Diez",
			"Veinte",
			"Treinta",
			"Cuarenta",
			"Cincuenta",
			"Sesenta",
			"Setenta",
			"Ochenta",
			"Noventa",
		];
		const especiales = [
			"Diez",
			"Once",
			"Doce",
			"Trece",
			"Catorce",
			"Quince",
			"Dieciséis",
			"Diecisiete",
			"Dieciocho",
			"Diecinueve",
		];
		const centenas = [
			"",
			"Ciento",
			"Doscientos",
			"Trescientos",
			"Cuatrocientos",
			"Quinientos",
			"Seiscientos",
			"Setecientos",
			"Ochocientos",
			"Novecientos",
		];

		function convertir(n) {
			let resultado = "";

			// Miles
			if (n >= 1000) {
				if (Math.floor(n / 1000) === 1) {
					resultado += "Mil ";
				} else {
					resultado += convertir(Math.floor(n / 1000)) + " Mil ";
				}
				n %= 1000;
			}

			// Centenas
			if (n >= 100) {
				if (n === 100) {
					return "Cien";
				}
				resultado += centenas[Math.floor(n / 100)] + " ";
				n %= 100;
			}

			// Decenas y unidades
			if (n > 0) {
				if (n < 10) {
					resultado += unidades[n];
				} else if (n < 20) {
					resultado += especiales[n - 10];
				} else {
					resultado += decenas[Math.floor(n / 10)];
					if (n % 10 !== 0) {
						resultado += " y " + unidades[n % 10];
					}
				}
			}

			return resultado.trim();
		}

		return `${convertir(num)} ${decPart}/100 ${getTextCurrency()}`;
	};

	const formatDescription = (description, name) => {
		if (!description) return name || "";

		if (typeof description === "object") {
			const entries = Object.entries(description);
			return (
				<>
					{name && <Text style={{ fontWeight: "bold", marginBottom: 5 }}>{name}</Text>}
					{entries.map(([key, value], idx) => (
						<React.Fragment key={idx}>
							<Text>
								<Text style={{ fontWeight: "bold" }}>{key}: </Text>
								{value}
							</Text>
						</React.Fragment>
					))}
				</>
			);
		}

		return description;
	};

	const totalItems = apiResponse.details.reduce((a, b) => a + b.qp, 0);
	const totalPrice = parseFloat(apiResponse.total || 0);
	const deliveryFee = parseFloat(apiResponse.delivery || 0);
	const grandTotal = totalPrice + deliveryFee;
	const totalEnLetras = numeroALetras(grandTotal);

	return (
		<Document author={apiResponse.client} title={apiResponse.client}>
			<Page size="A4" style={styles.page}>
				{/* Encabezado */}
				<View style={styles.headerContainer}>
					<View style={styles.headerLeft}>
						<View style={{ position: "relative", top: 7 }}>
							<Image src={`${API_url}${apiResponse.logo}`} style={styles.logo} />
						</View>
						<View>
							<Text>Empresa: {apiResponse.cName}</Text>
							<Text>Dirección: {apiResponse.address}</Text>
							<View style={{ flexDirection: "row", marginTop: "2px" }}>
								<Text>NIT: {apiResponse.cNit} </Text>
								<Text style={{ paddingLeft: 15 }}>Tel: {apiResponse.phone}</Text>
							</View>
						</View>
					</View>
					<View style={{ justifyContent: "center", alignItems: "center" }}>
						<Text style={styles.titleBig}>COTIZACION</Text>
					</View>
					<View style={{ justifyContent: "center", alignItems: "center" }}>
						<Text style={styles.titleNumber}>N°{apiResponse.nro}</Text>
					</View>
				</View>

				{/* Sub-encabezado */}
				<View style={styles.subHeaderContainer}>
					<View>
						<Text>
							Vendedor: {apiResponse.seller} {/* Tel: {apiResponse.seller_phone} */}
						</Text>
						<Text>
							Cliente: {apiResponse.client} Tel: {apiResponse.client_phone}
						</Text>
					</View>
					<View
						style={{
							display: "flex",
							flexDirection: "column",
							gap: "5",
							position: "relative",
							bottom: 0,
						}}>
						<Text>
							Fecha: {apiResponse.oDate} - {apiResponse.oTime}
						</Text>
						<Text>Vigencia: {calcularVigencia(apiResponse.oDate, apiResponse.validity)}</Text>
					</View>
				</View>

				{/* Tabla */}
				<View style={styles.tableContainer}>
					<View style={styles.tableHeader}>
						<Text style={[styles.tableHeaderCell, styles.tableCellNro]}>Nro</Text>
						<Text style={[styles.tableHeaderCell, styles.tableCellCodigo]}>Código</Text>
						<Text style={[styles.tableHeaderCell, styles.tableCellDescripcion]}>
							Descripción
						</Text>
						<Text style={[styles.tableHeaderCell, styles.tableCellCantidad]}>Cant</Text>
						<Text style={[styles.tableHeaderCell, styles.tableCellPrecioUnitario]}>
							Precio Unitario
						</Text>
						<Text style={[styles.tableHeaderCell, styles.tableCellDescuento]}>Descuento</Text>
						<Text style={[styles.tableHeaderCell, styles.tableCellPrecio]}>Precio</Text>
					</View>
					{apiResponse?.details.map((item, index) => {
						const priceU = getPrice(item.priceR, (apiResponse.bill === "enabled"), item?.priceB ?? 0);
						const priceWB = getPrice(item.priceR, true, item?.priceB ?? 0); //price with billy

						const discount = parseFloat(item.discount_price || 0);
						const totalSinFactura = getTotalSinFactura(item.priceR, discount, item.qp);
						const totalConFactura = getTotalConFactura(
							item.priceR,
							item.priceB,
							discount,
							item.qp
						);
						return (
							<React.Fragment key={item.code}>
								{/* First row - Sin Factura */}
								<View style={styles.tableRow}>
									<Text style={[styles.tableCell, styles.tableCellNro]}>{index + 1}</Text>
									<Text style={[styles.tableCell, styles.tableCellCodigo]}>
										{item.code}
									</Text>
									<View style={[styles.tableCell, styles.tableCellDescripcion]}>
										<View
											style={{
												flexDirection: "row",
												justifyContent: "space-between",
												alignItems: "center",
											}}>
											<Text style={{ flex: 1 }}>{item.product_name}</Text>
											<Text
												style={{
													fontSize: 7,
													fontFamily: "Helvetica-Bold",
												}}>
												{apiResponse.bill === "Sin Factura"
													? "Ambos"
													: apiResponse.bill === "enabled"
														? "Facturado"
														: "Sin Factura"}
											</Text>
										</View>
										{typeof item.description === "object" ? (
											formatDescription(item.description, item.name)
										) : (
											<Text>{item.description || ""}</Text>
										)}
									</View>
									<Text style={[styles.tableCell, styles.tableCellCantidad]}>
										{item.qp}
									</Text>
									<Text style={[styles.tableCell, styles.tableCellPrecioUnitario]}>
										{convertPrice(priceU, null, true)}
									</Text>
									<Text style={[styles.tableCell, styles.tableCellDescuento]}>
										{discount.toFixed(2)}
									</Text>
									<Text style={[styles.tableCell, styles.tableCellPrecio]}>
										{convertPrice(apiResponse.bill === "enabled" ? totalConFactura : totalSinFactura, null, true)}
									</Text>
								</View>

								{/* Second row - Facturado (only when bill is "both") */}
								{apiResponse.bill === "both" && (
									<View style={[styles.tableRow, { backgroundColor: "#f9f9f9" }]}>
										<Text style={[styles.tableCell, styles.tableCellNro]}></Text>
										<Text style={[styles.tableCell, styles.tableCellCodigo]}></Text>
										<View style={[styles.tableCell, styles.tableCellDescripcion]}>
											<View
												style={{
													flexDirection: "row",
													justifyContent: "space-between",
													alignItems: "center",
												}}>
												<Text style={{ flex: 1 }}>{""}</Text>
												<Text
													style={{
														fontSize: 7,
														fontFamily: "Helvetica-Bold",
													}}>
													Facturado
												</Text>
											</View>
										</View>
										<Text style={[styles.tableCell, styles.tableCellCantidad]}>
											{item.qp}
										</Text>
										<Text style={[styles.tableCell, styles.tableCellPrecioUnitario]}>
											{convertPrice(priceWB, null, true)}
										</Text>
										<Text style={[styles.tableCell, styles.tableCellDescuento]}>
											{convertPrice(discount, null, true)}
										</Text>
										<Text style={[styles.tableCell, styles.tableCellPrecio]}>
											{convertPrice(totalConFactura, null, true)}
										</Text>
									</View>
								)}
							</React.Fragment>
						);
					})}{" "}
				</View>

				{/* Línea azul y totales */}
				<View style={styles.bottomRowContainer}>
					<View style={styles.bottomNoteContainer}>
						<Text style={styles.bottomNote}>
							{apiResponse.warranty
								? `Garantía válida hasta: ${apiResponse.warranty}`
								: "Si en caso venciera su proforma de la fecha límite, deberá actualizar su proforma."}
						</Text>
					</View>

					<View style={styles.bottomTotals}>
						<View style={styles.bottomTotalsItem}>
							<Text>Total:</Text>
							<Text style={styles.boldText}>{convertPrice(totalPrice)}</Text>
						</View>
						{deliveryFee > 0 && (
							<View style={styles.bottomTotalsItem}>
								<Text>Delivery:</Text>32 . . 0<Text>{convertPrice(deliveryFee)}</Text>.
							</View>
						)}
						<View style={styles.bottomTotalsItem}>
							<Text>Total a pagar:</Text>
							<Text style={styles.boldText}>{convertPrice(grandTotal)}</Text>
						</View>
						<View style={styles.bottomTotalsItem}>
							<Text>Factura:</Text>
							<Text>{getBill(apiResponse.bill)}</Text>
						</View>
					</View>
				</View>

				{/* Línea con Items y Venta Total */}
				<View style={styles.bottomDetailsRow}>
					<Text style={styles.bottomDetailsText}>
						Items: {totalItems}{" "}
						{/*{cotizacion.bill === "disabled" ? "No requiere factura" : "Requiere factura"}*/}
						{" • Cotizacion total de: "}
						{totalEnLetras}
					</Text>
				</View>

				<View style={styles.signatureContainer}>
					<View style={styles.signatureBox}>
						<Text style={styles.signatureLine}>___________________________</Text>
						<Text>{apiResponse.seller}</Text>
						<Text>Vendedor</Text>
					</View>
					<View style={styles.signatureBox}>
						<Text style={styles.signatureLine}>___________________________</Text>
						<Text>{apiResponse.client}</Text>
						<Text>Cliente</Text>
					</View>
				</View>

				<Text
					style={styles.pageNumbers}
					render={({ pageNumber, totalPages }) => `${pageNumber} / ${totalPages}`}
					fixed
				/>
			</Page>
		</Document>
	);
};

export const GeneratePdfCotizacion = ({ apiResponse, isOpen, onClose, themeColor, convertPrice = (() => { }) }) => {
	if (!apiResponse || Object.entries(apiResponse).length === 0) return null;

	const handleDownloadPdf = async () => {
		if (!apiResponse) return;

		try {
			// Generate the PDF blob
			const asPdf = pdf(
				<CotizacionPDF apiResponse={apiResponse} themeColor={themeColor} convertPrice={convertPrice} />
			);
			const blob = await asPdf.toBlob();

			// Create a download link
			const fileURL = URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = fileURL;

			// Set the custom filename
			link.download = `Cotizacion_${apiResponse.client}.pdf`;

			// Trigger download
			link.click();

			// Clean up
			URL.revokeObjectURL(fileURL);
		} catch (error) {
			console.error("Error generating PDF:", error);
		}
	};

	return (
		<Dialog fullScreen open={isOpen}>
			<AppBar sx={{ position: "relative" }}>
				<Toolbar>
					<IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
						<CloseIcon />
					</IconButton>
					<Button color="inherit" startIcon={<GetAppIcon />} onClick={handleDownloadPdf} sx={{ ml: "auto" }}>
						Descargar PDF
					</Button>
				</Toolbar>
			</AppBar>
			{apiResponse && (
				<PDFViewer showToolbar={true} width="100%" height="100%">
					<CotizacionPDF apiResponse={apiResponse} themeColor={themeColor} convertPrice={convertPrice} />
				</PDFViewer>
			)}
		</Dialog>
	);
};
